import React, { useState } from 'react';
import { 
  User,
  Image,
  Loader2,
  ExternalLink,
  CheckCircle,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useProviderFeatures } from '../hooks/useFeatureFlags';
import toast from 'react-hot-toast';

const ProviderSelection = ({ 
  user, 
  title = "Connect Your Photo Platform",
  subtitle = "Choose a platform to connect and start enhancing your photos",
  showExisting = true,
  className = "",
  compact = false
}) => {
  const { initiateAuth, disconnectProvider } = useAuthStore();
  const { enabledProviders, isSmugMugEnabled, isFlickrEnabled, isUnsplashEnabled } = useProviderFeatures();
  const [connectingProvider, setConnectingProvider] = useState('');
  const [disconnectingProvider, setDisconnectingProvider] = useState('');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(null);

  // Helper function to get provider display name and info
  const getProviderInfo = (provider) => {
    const providers = {
      'flickr': { 
        name: 'Flickr', 
        color: 'bg-blue-600 hover:bg-blue-700',
        description: 'Full metadata control for all your photos',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.5 8.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zm13 0a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z"/>
          </svg>
        )
      },
      'unsplash': { 
        name: 'Unsplash', 
        color: 'bg-black hover:bg-gray-800',
        description: 'Update metadata for your own uploaded photos',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.5 6.5C7.5 3.46 9.96 1 13 1s5.5 2.46 5.5 5.5S16.04 12 13 12 7.5 9.54 7.5 6.5zM13 13.5c3.04 0 5.5 2.46 5.5 5.5S16.04 24.5 13 24.5 7.5 22.04 7.5 19s2.46-5.5 5.5-5.5z"/>
          </svg>
        )
      },
      'smugmug': { 
        name: 'SmugMug', 
        color: 'bg-green-600 hover:bg-green-700',
        description: 'Professional gallery management',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
          </svg>
        )
      }
    };
    return providers[provider] || { name: provider, color: 'bg-gray-600 hover:bg-gray-700', description: '', icon: null };
  };

  // Handle connecting a new provider
  const handleConnectProvider = async (provider) => {
    setConnectingProvider(provider);
    try {
      await initiateAuth(provider);
    } catch (error) {
      console.error(`Failed to initiate ${provider} auth:`, error);
      setConnectingProvider('');
    }
  };

  // Handle disconnecting a provider
  const handleDisconnectProvider = async (provider) => {
    setDisconnectingProvider(provider);
    try {
      const result = await disconnectProvider(provider);
      if (result.success) {
        toast.success(result.message);
        setShowDisconnectConfirm(null);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error(`Failed to disconnect ${provider}:`, error);
      toast.error(`Failed to disconnect ${provider} account`);
    } finally {
      setDisconnectingProvider('');
    }
  };

  // Show confirmation dialog for disconnect
  const confirmDisconnect = (provider) => {
    setShowDisconnectConfirm(provider);
  };

  // Get all available providers for connection (filtered by feature flags)
  const getAvailableProviders = () => {
    const connectedProviders = user?.connectedAccounts?.map(acc => acc.provider) || [];
    
    // Only include providers that are enabled via feature flags
    const allProviders = [];
    if (isFlickrEnabled) allProviders.push('flickr');
    if (isUnsplashEnabled) allProviders.push('unsplash'); 
    if (isSmugMugEnabled) allProviders.push('smugmug');
    
    return allProviders.filter(provider => !connectedProviders.includes(provider));
  };

  const connectedAccounts = user?.connectedAccounts || [];
  const availableProviders = getAvailableProviders();

  // Determine if we should show the header section
  const showHeader = title || subtitle;
  
  return (
    <div className={compact ? className : `bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header Section - only show if title or subtitle provided and not compact */}
      {showHeader && !compact && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <User className="mr-2 h-5 w-5" />
            {title}
          </h2>
          {subtitle && (
            <p className="text-gray-600 mt-1 text-sm">
              {subtitle}
            </p>
          )}
        </div>
      )}
      
      <div className={compact ? "" : "px-6 py-4"}>
        {/* Show existing connected accounts if requested and they exist */}
        {showExisting && connectedAccounts.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Connected Accounts</h4>
            <div className="space-y-3">
              {connectedAccounts.map((account) => {
                const providerInfo = getProviderInfo(account.provider);
                return (
                  <div key={account._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${providerInfo.color.split(' ')[0]}`}>
                        {providerInfo.icon}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{providerInfo.name}</h3>
                        <p className="text-xs text-gray-600">@{account.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Connected
                      </span>
                      {account.profileUrl && (
                        <a
                          href={account.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title={`View ${providerInfo.name} profile`}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {/* Show disconnect button for all connected accounts */}
                      <button
                        onClick={() => confirmDisconnect(account.provider)}
                        disabled={disconnectingProvider === account.provider}
                        className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title={`Disconnect ${providerInfo.name} account`}
                      >
                        {disconnectingProvider === account.provider ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {availableProviders.length > 0 && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Add Another Account</h4>
              </div>
            )}
          </div>
        )}

        {/* Show available providers for connection */}
        {availableProviders.length > 0 ? (
          <div className={`grid gap-3 ${compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1'}`}>
            {availableProviders.map((provider) => {
              const providerInfo = getProviderInfo(provider);
              return (
                <button
                  key={provider}
                  onClick={() => handleConnectProvider(provider)}
                  disabled={connectingProvider === provider}
                  className={`${
                    compact 
                      ? 'flex flex-col items-center justify-center p-4 border border-gray-300 rounded-lg text-sm font-medium text-white transition-colors hover:shadow-md'
                      : 'flex items-center justify-between p-4 border border-gray-300 rounded-lg text-sm font-medium text-white transition-colors hover:shadow-md'
                  } ${providerInfo.color} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {compact ? (
                    // Compact layout: icon and text stacked vertically
                    <>
                      <div className="flex items-center justify-center mb-2">
                        {providerInfo.icon}
                      </div>
                      <div className="text-center">
                        <div className="font-medium">Connect {providerInfo.name}</div>
                      </div>
                      <div className="mt-2 flex items-center">
                        {connectingProvider === provider ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-3 h-3" />
                        )}
                      </div>
                    </>
                  ) : (
                    // Full layout: horizontal with description
                    <>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center">
                          {providerInfo.icon}
                        </div>
                        <div className="text-left">
                          <div className="font-medium">Connect {providerInfo.name}</div>
                          <div className="text-xs text-white/80">{providerInfo.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {connectingProvider === provider ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          showExisting && connectedAccounts.length > 0 && (
            <div className="text-center py-4">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">All available platforms connected</p>
            </div>
          )
        )}

        {/* Empty state when no accounts and no available providers */}
        {connectedAccounts.length === 0 && availableProviders.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Photo Platforms Available</h3>
            <p className="text-gray-600">Please configure API credentials in your environment settings</p>
          </div>
        )}

        {/* Confirmation Dialog for Disconnect */}
        {showDisconnectConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDisconnectConfirm(null)}>
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Disconnect {getProviderInfo(showDisconnectConfirm).name}?
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to disconnect your {getProviderInfo(showDisconnectConfirm).name} account? 
                You won't be able to enhance photos from this platform until you reconnect.
                {connectedAccounts.length === 1 && (
                  <span className="block mt-2 text-amber-600 font-medium">
                    ⚠️ This is your only connected account. You'll need to reconnect a provider to use the service again.
                  </span>
                )}
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDisconnectConfirm(null)}
                  disabled={disconnectingProvider === showDisconnectConfirm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDisconnectProvider(showDisconnectConfirm)}
                  disabled={disconnectingProvider === showDisconnectConfirm}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
                >
                  {disconnectingProvider === showDisconnectConfirm ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderSelection;
